import { useQuery } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import type { AdminApi, AppInstanceSummary, AppPushCapabilities } from '../../../api/client.ts';

/**
 * What the push composer reads about the environment (BB-77): its push defaults, the registry of Android channels,
 * iOS categories and threads, the apps and what each iOS app declares (time-sensitive entitlement, Notification
 * Service Extension). Archived registry entries stay listed so a message that still names one can show it.
 */
export function usePushRegistry(environmentId: string) {
  const { api } = useRouter().options.context as { api: AdminApi };
  const settings = useQuery({ queryKey: ['push-settings', environmentId], queryFn: () => api.pushSettings(environmentId), staleTime: 60_000 });
  const channels = useQuery({ queryKey: ['push-android-channels', environmentId], queryFn: () => api.pushAndroidChannels(environmentId), staleTime: 60_000 });
  const categories = useQuery({ queryKey: ['push-ios-categories', environmentId], queryFn: () => api.pushIosCategories(environmentId), staleTime: 60_000 });
  const threads = useQuery({ queryKey: ['push-ios-threads', environmentId], queryFn: () => api.pushIosThreads(environmentId), staleTime: 60_000 });
  const apps = useQuery({ queryKey: ['apps', environmentId], queryFn: () => api.listApps(environmentId) });
  const iosApps = (apps.data ?? []).filter((a) => a.platform === 'ios' && a.status === 'active');
  const capabilities = useQuery({
    queryKey: ['push-capabilities', environmentId, iosApps.map((a) => a.id)],
    enabled: apps.isSuccess,
    queryFn: async () => Object.fromEntries(await Promise.all(iosApps.map(async (a) => [a.id, await api.appPushCapabilities(environmentId, a.id)] as const))) as Record<string, AppPushCapabilities>,
    staleTime: 60_000,
  });
  /** `undefined` while it is not known yet. */
  const someIosApp = (flag: keyof Pick<AppPushCapabilities, 'iosTimeSensitiveEnabled' | 'iosNotificationServiceExtension'>) =>
    capabilities.data ? Object.values(capabilities.data).some((c) => c[flag]) : undefined;
  return {
    settings,
    channels,
    categories,
    threads,
    apps,
    capabilities,
    /** Active apps of the platforms, for «only these apps». */
    appsOf: (platforms: readonly string[]): AppInstanceSummary[] => (apps.data ?? []).filter((a) => a.status === 'active' && platforms.includes(a.platform)),
    timeSensitiveAvailable: someIosApp('iosTimeSensitiveEnabled'),
    imageExtensionAvailable: someIosApp('iosNotificationServiceExtension'),
  };
}

export type PushRegistry = ReturnType<typeof usePushRegistry>;
